import { AxiosRequestConfig } from 'axios';
import { stringify } from 'qs';
import { apiClient } from './client';
import { withAuthorizationHeader } from './auth';
import { ValidatedCart } from '../domain/cart';
import { Order, OrderStatus, Refund } from '../domain/order';
import { ListResponse } from '../domain/list';
import { BaseQueryString } from '../domain/pagination';

export type CreateOrderPayload = {
  cart: ValidatedCart;
  marketId: string;
  cooperativeId: string;
  notes: string;
  deliveryDate: string;
  appliedVoucherCode?: string;
};

export const createOrder = async (
  token: string,
  payload: CreateOrderPayload,
  opts: AxiosRequestConfig = {},
): Promise<Order[]> => {
  const response = await apiClient.post<{ data: Order[] }>(
    '/v1/orders',
    {
      ...payload.cart,
      ...payload,
    },
    withAuthorizationHeader(token, opts),
  );
  return response.data.data;
};

export const readOrder = async (
  token: string,
  orderId: string,
  opts: AxiosRequestConfig = {},
): Promise<Order> => {
  const response = await apiClient.get<Order>(
    `/v1/orders/${orderId}`,
    withAuthorizationHeader(token, opts),
  );
  return response.data;
};

export type GetOrdersQuery = BaseQueryString & {
  cooperativeId?: string;
  marketId?: string;
  status?: OrderStatus | 'ALL';
};

export const searchOrders = async (
  token: string,
  query: GetOrdersQuery,
  opts: AxiosRequestConfig = {},
): Promise<ListResponse<Order>> => {
  const { pageNo = 1, pageSize = 25, ...rest } = query;
  const queryString = stringify({
    pageNo,
    pageSize,
    ...rest,
  });
  const response = await apiClient.get<ListResponse<Order>>(
    `/v1/orders?${queryString}`,
    withAuthorizationHeader(token, opts),
  );
  return response.data;
};

export type OrderAction =
  | 'CONFIRM_ORDER'
  | 'SHIP_ORDER'
  | 'DELIVER_ORDER'
  | 'COMPLETE_ORDER'
  | 'CANCEL_ORDER';

export type UpdateOrderStatusPayload = {
  orderAction: OrderAction;
  context?: {
    origin: 'chatbox' | 'details';
    data?: Record<string, unknown>;
  };
};

export const updateOrderStatus = async (
  token: string,
  orderId: string,
  payload: UpdateOrderStatusPayload,
  opts: AxiosRequestConfig = {},
): Promise<Order> => {
  const response = await apiClient.patch<{ data: Order }>(
    `/v1/orders/${orderId}/status`,
    payload,
    withAuthorizationHeader(token, opts),
  );
  return response.data.data;
};

export const createOrderRefund = async (
  token: string,
  orderId: string,
  payload: Partial<Refund>,
  opts: AxiosRequestConfig = {},
): Promise<Refund> => {
  const response = await apiClient.post<Refund>(
    `/v1/orders/${orderId}/refund`,
    payload,
    withAuthorizationHeader(token, opts),
  );
  return response.data;
};

export const getRefunds = async (
  token: string,
  query: {
    pageNo?: number;
    pageSize?: number;
    status?: string;
    requestType?: string;
    marketId?: string;
    cooperativeId?: string;
  } = {},
  opts: AxiosRequestConfig = {},
): Promise<ListResponse<Refund>> => {
  const { pageNo = 1, pageSize = 25, ...rest } = query;
  const queryString = stringify({
    pageNo,
    pageSize,
    ...rest,
  });
  const response = await apiClient.get<ListResponse<Refund>>(
    `/v1/refunds?${queryString}`,
    withAuthorizationHeader(token, opts),
  );
  return response.data;
};

export const updateRefundStatus = async (
  token: string,
  orderId: string,
  refundId: string,
  payload: { refundAction: string },
  opts: AxiosRequestConfig = {},
): Promise<Refund> => {
  const response = await apiClient.patch<Refund>(
    `/v1/orders/${orderId}/refund/${refundId}/status`,
    payload,
    withAuthorizationHeader(token, opts),
  );
  return response.data;
};
